import React from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../i18n/index.ts";

interface LanguageSwitcherProps {
  variant?: "compact" | "full";
}

const FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const LANG_LABEL: Record<SupportedLanguage, string> = {
  de: "DE",
  en: "EN",
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = "compact",
}) => {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? i18n.language ?? "de") as
    | SupportedLanguage
    | string;

  const change = (lng: SupportedLanguage) => {
    if (lng === current) return;
    i18n.changeLanguage(lng);
  };

  return (
    <div
      role="group"
      aria-label="Language"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: variant === "compact" ? "999px" : "10px",
        padding: "2px",
        fontFamily: FONT,
      }}
    >
      {SUPPORTED_LANGUAGES.map((lng) => {
        const active = current.startsWith(lng);
        return (
          <button
            key={lng}
            type="button"
            onClick={() => change(lng)}
            style={{
              padding: variant === "compact" ? "5px 10px" : "8px 14px",
              background: active
                ? "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)"
                : "transparent",
              color: active ? "white" : "rgba(255,255,255,0.7)",
              border: "none",
              borderRadius: variant === "compact" ? "999px" : "8px",
              fontSize: variant === "compact" ? "12px" : "14px",
              fontWeight: 700,
              cursor: active ? "default" : "pointer",
              letterSpacing: "0.05em",
              fontFamily: FONT,
              transition: "background 0.15s ease",
            }}
            aria-pressed={active}
          >
            {LANG_LABEL[lng]}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
